import React, { createContext, useState, useRef, useEffect, useContext, useCallback, ReactNode } from 'react';
import { ExhibitMediaItem } from '../types/models'; // Assuming this type exists

// Define the shape of the context state
interface AudioPlayerState {
    currentMedia: ExhibitMediaItem | null;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    isLoading: boolean;
    error: Error | null;
    volume: number;
    isMuted: boolean;
    playbackRate: number;
    isTranscriptVisible: boolean; // New state for transcript visibility
}

// Define the shape of the context actions
interface AudioPlayerActions {
    loadAudio: (media: ExhibitMediaItem, autoPlay?: boolean) => void;
    play: () => void;
    pause: () => void;
    seek: (time: number) => void;
    setVolume: (volume: number) => void;
    toggleMute: () => void;
    setPlaybackRate: (rate: number) => void;
    unloadAudio: () => void;
    toggleTranscriptVisibility: () => void; // New action
}

// Combine state and actions for the context value
type AudioPlayerContextType = AudioPlayerState & AudioPlayerActions;

// Create the context with a default value (can be undefined or a default object)
const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

// Define props for the provider
interface AudioPlayerProviderProps {
    children: ReactNode;
}

// Create the provider component
export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
    const [currentMedia, setCurrentMedia] = useState<ExhibitMediaItem | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [isTranscriptVisible, setIsTranscriptVisible] = useState(false); // Initialize transcript state

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [shouldAutoPlay, setShouldAutoPlay] = useState(false);

    // Initialize Audio Element
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            // Set initial properties if needed
            audioRef.current.volume = volume;
            audioRef.current.muted = isMuted;
            audioRef.current.playbackRate = playbackRate;
        }
        const audio = audioRef.current;

        // Event Listeners
        const handleLoadedMetadata = () => {
            setDuration(audio.duration);
            setIsLoading(false);
            setError(null);
            if (shouldAutoPlay) {
                audio.play().catch(e => console.warn("Auto-play failed:", e));
                setShouldAutoPlay(false); // Reset auto-play flag
            }
        };

        const handleError = (e: Event) => {
            console.error('Global Audio error:', e);
            const errorMessage = 'Failed to load audio file';
            setError(new Error(errorMessage));
            setIsLoading(false);
            setCurrentMedia(null); // Clear media on error
        };

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };

        const handlePlayPause = () => {
            setIsPlaying(!audio.paused);
        };

        const handleVolumeChange = () => {
            setVolume(audio.volume);
            setIsMuted(audio.muted);
        };

        const handleRateChange = () => {
            setPlaybackRate(audio.playbackRate);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            // Optionally: setCurrentTime(0); or load next track?
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('error', handleError);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('play', handlePlayPause);
        audio.addEventListener('pause', handlePlayPause);
        audio.addEventListener('volumechange', handleVolumeChange);
        audio.addEventListener('ratechange', handleRateChange);
        audio.addEventListener('ended', handleEnded);

        // Cleanup
        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('play', handlePlayPause);
            audio.removeEventListener('pause', handlePlayPause);
            audio.removeEventListener('volumechange', handleVolumeChange);
            audio.removeEventListener('ratechange', handleRateChange);
            audio.removeEventListener('ended', handleEnded);
            // Consider pausing and resetting src on unmount?
            // audio.pause();
            // audio.removeAttribute('src');
        };
    }, [shouldAutoPlay]); // Re-run if shouldAutoPlay changes

    // --- Action Implementations ---

    const loadAudio = useCallback((media: ExhibitMediaItem, autoPlay = false) => {
        if (!audioRef.current) return;
        if (currentMedia?.id === media.id) {
             // If same media, just play/pause or seek
             if (autoPlay && !isPlaying) {
                 play();
             }
             return;
        }

        console.log('Loading new audio:', media.file_path);
        setError(null);
        setIsLoading(true);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        setCurrentMedia(media);
        setShouldAutoPlay(autoPlay); // Set flag for auto-play after loading

        audioRef.current.src = media.file_path;
        audioRef.current.load(); // Important: load the new source
    }, [currentMedia, isPlaying]);

    const play = useCallback(() => {
        if (audioRef.current && !isPlaying) {
            audioRef.current.play().catch(e => {
                console.error("Play error:", e);
                setError(new Error("Failed to play audio"));
            });
        }
    }, [isPlaying]);

    const pause = useCallback(() => {
        if (audioRef.current && isPlaying) {
            audioRef.current.pause();
        }
    }, [isPlaying]);

    const seek = useCallback((time: number) => {
        if (audioRef.current && isFinite(time)) {
            audioRef.current.currentTime = Math.max(0, Math.min(time, duration));
        }
    }, [duration]);

    const setVolumeCallback = useCallback((newVolume: number) => {
        if (audioRef.current) {
            const clampedVolume = Math.max(0, Math.min(newVolume, 1));
            audioRef.current.volume = clampedVolume;
            // Automatically unmute if volume is set > 0
            if (clampedVolume > 0 && audioRef.current.muted) {
                audioRef.current.muted = false;
            }
        }
    }, []);

    const toggleMuteCallback = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.muted = !audioRef.current.muted;
        }
    }, []);

    const setPlaybackRateCallback = useCallback((rate: number) => {
        if (audioRef.current) {
            audioRef.current.playbackRate = rate;
        }
    }, []);

    const unloadAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.removeAttribute('src'); // Remove source
            // Reset state
            setCurrentMedia(null);
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);
            setIsLoading(false);
            setError(null);
        }
    }, []);

    const toggleTranscriptVisibilityCallback = useCallback(() => {
        setIsTranscriptVisible(prev => !prev);
    }, []);

    // Memoize context value
    const contextValue = React.useMemo(() => ({
        currentMedia,
        isPlaying,
        currentTime,
        duration,
        isLoading,
        error,
        volume,
        isMuted,
        playbackRate,
        isTranscriptVisible, // Add to context value
        loadAudio,
        play,
        pause,
        seek,
        setVolume: setVolumeCallback,
        toggleMute: toggleMuteCallback,
        setPlaybackRate: setPlaybackRateCallback,
        unloadAudio,
        toggleTranscriptVisibility: toggleTranscriptVisibilityCallback, // Add action to context value
    }), [
        currentMedia, isPlaying, currentTime, duration, isLoading, error, volume, isMuted, playbackRate, isTranscriptVisible,
        loadAudio, play, pause, seek, setVolumeCallback, toggleMuteCallback, setPlaybackRateCallback, unloadAudio, toggleTranscriptVisibilityCallback
    ]);

    return (
        <AudioPlayerContext.Provider value={contextValue}>
            {children}
        </AudioPlayerContext.Provider>
    );
};

// Custom hook to use the context
export const useAudioPlayer = (): AudioPlayerContextType => {
    const context = useContext(AudioPlayerContext);
    if (context === undefined) {
        throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
    }
    return context;
};
